"use client";

import Image from "next/image";
import Link from "next/link";
import { useRef, useState, type ReactNode } from "react";
import ActionButtons from "@/components/shared/navbar/ActionButtons";
import { loginFlow } from "@/components/shared/modals";
import DynamicModal from "@/components/shared/modals/DynamicModal";
import { ModalFlow } from "@/types/components/modal";
import { FORM_TYPES, LOGIN_LEAD_TRIGGER_SOURCES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { MarketplaceMobileSearchBar } from "./MarketplaceMobileSearchBar";
import { useMarketplaceMobilePinnedSearch } from "./useMarketplaceMobilePinnedSearch";
import type { MarketplaceSearchResult } from "@/types/api/marketplace";

interface MarketplaceMobileHeaderProps {
  searchSuggestions: MarketplaceSearchResult[];
  searchValue: string;
  onSearchChange: (query: string) => void;
  onSearchCommit: (query: string) => void;
  onSearchSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  debouncedSearch?: string;
  isSearchFetching?: boolean;
  children: ReactNode;
}

export function MarketplaceMobileHeader({
  searchSuggestions,
  searchValue,
  onSearchChange,
  onSearchCommit,
  onSearchSuggestionSelect,
  debouncedSearch = "",
  isSearchFetching = false,
  children,
}: MarketplaceMobileHeaderProps) {
  const logoRowRef = useRef<HTMLDivElement>(null);
  const pinnedBlockRef = useRef<HTMLDivElement>(null);
  const { pinOffset, isPinned, pinnedBlockHeight } =
    useMarketplaceMobilePinnedSearch(logoRowRef, pinnedBlockRef);

  const [modalFlow, setModalFlow] = useState<ModalFlow | null>(null);
  const [postLoginRedirect, setPostLoginRedirect] = useState<string | null>(
    null
  );

  const openModal = (flow: ModalFlow | null) => setModalFlow(flow);
  const closeModal = () => setModalFlow(null);

  const handleLoginClick = (redirectUrl?: string) => {
    if (redirectUrl) setPostLoginRedirect(redirectUrl);
    openModal(loginFlow());
  };

  return (
    <div className="md:hidden">
      <div
        ref={logoRowRef}
        className="app-container border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-3 py-2">
          <Link href="/" className="flex shrink-0">
            <Image
              src="/icons/logo.svg"
              alt="Travel LYKKE Logo"
              width={110}
              height={40}
              className="h-10 w-[82px] sm:w-[102px]"
              priority
            />
          </Link>

          <ActionButtons
            onLoginClick={handleLoginClick}
            showSearchInNav={false}
            showMyBookings={false}
          />
        </div>
      </div>

      {pinnedBlockHeight > 0 ? (
        <div aria-hidden style={{ height: pinnedBlockHeight }} />
      ) : null}

      <div
        ref={pinnedBlockRef}
        className={cn(
          "fixed top-0 right-0 left-0 z-50 w-full bg-white will-change-transform transition-shadow duration-300 ease-out",
          isPinned ? "shadow-sm" : "shadow-none"
        )}
        style={{ transform: `translateY(${pinOffset}px)` }}>
        <div className="app-container pt-2 pb-0">
          <MarketplaceMobileSearchBar
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onSearchCommit={onSearchCommit}
            onSearchSuggestionSelect={onSearchSuggestionSelect}
            searchSuggestions={searchSuggestions}
            debouncedSearch={debouncedSearch}
            isSearchFetching={isSearchFetching}
          />
        </div>

        {children}
      </div>

      {modalFlow && (
        <DynamicModal
          flow={modalFlow}
          isOpen={!!modalFlow}
          onClose={closeModal}
          metadata={{
            form_name: FORM_TYPES.LOGIN_FORM,
            page_url: window.location.pathname,
            referring_callout:
              postLoginRedirect === "/ai-planner"
                ? LOGIN_LEAD_TRIGGER_SOURCES.PLAN_WITH_AI
                : LOGIN_LEAD_TRIGGER_SOURCES.LOGIN_BUTTON,
          }}
        />
      )}
    </div>
  );
}
